// ============================================================================
// route-order — TSP-style visit ordering for the (gated) Route Optimizer.
//
// LOGIC ONLY. Ben authorized building the ordering algorithm 2026-07-31;
// the Route Optimizer feature itself (UI, surfaces, persistence) is DESIGNED
// but its build is GATED on Ben's approval — see
// docs/route-optimizer-design-2026-07-31.md. Nothing imports this module yet.
//
// Approach (per the May-13 route-planner spec, confirmed in the design doc):
// greedy nearest-neighbour construction + 2-opt refinement. No external
// solver. Distances are haversine ("as the crow flies") — good enough to get
// the ORDER right in a metro; real drive times come later from the Routes API
// via mapsProxy and only re-rank near-ties (design doc §4).
//
// Deliberately capped at 30 stops: 2-opt is O(n²) per pass and TSP quality
// degrades noticeably past that; the May spec says prompt the user to filter
// further, so the caller gets a typed error rather than a slow bad answer.
// ============================================================================

export interface RouteStop {
  id: string;
  lat: number;
  lng: number;
}

export interface OrderedRoute {
  /** stop ids in visit order (excludes the virtual start point) */
  order: string[];
  /** total distance in miles, including the leg from `start` if given,
   *  and the return leg when roundTrip */
  totalMiles: number;
  legs: { fromId: string | null; toId: string; miles: number }[];
}

export interface OrderRouteOptions {
  /** where the driver begins (e.g. geolocation or office); not a stop */
  start?: { lat: number; lng: number };
  /** end back at start (adds the return leg to the total) */
  roundTrip?: boolean;
}

export const MAX_ROUTE_STOPS = 30;

const EARTH_RADIUS_MILES = 3958.8;

export function haversineMiles(
  aLat: number,
  aLng: number,
  bLat: number,
  bLng: number,
): number {
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(bLat - aLat);
  const dLng = toRad(bLng - aLng);
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(aLat)) * Math.cos(toRad(bLat)) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_MILES * Math.asin(Math.sqrt(s));
}

export class TooManyStopsError extends RangeError {
  constructor(count: number) {
    super(
      `Route ordering supports up to ${MAX_ROUTE_STOPS} stops (got ${count}). ` +
        'Filter the selection down before optimizing.',
    );
    this.name = 'TooManyStopsError';
  }
}

/**
 * Order stops into a short visit sequence.
 *
 * Deterministic for a given input. Throws TooManyStopsError above
 * MAX_ROUTE_STOPS. Duplicate coordinates are fine (zero-length legs).
 */
export function orderRoute(
  stops: RouteStop[],
  options: OrderRouteOptions = {},
): OrderedRoute {
  if (stops.length > MAX_ROUTE_STOPS) throw new TooManyStopsError(stops.length);
  if (stops.length === 0) return { order: [], totalMiles: 0, legs: [] };

  const { start, roundTrip = false } = options;
  const n = stops.length;

  // distance matrix (n <= 30, so at most 900 cells — trivial)
  const d: number[][] = Array.from({ length: n }, () => new Array<number>(n));
  for (let i = 0; i < n; i++) {
    for (let j = i; j < n; j++) {
      const m =
        i === j
          ? 0
          : haversineMiles(stops[i].lat, stops[i].lng, stops[j].lat, stops[j].lng);
      d[i][j] = m;
      d[j][i] = m;
    }
  }
  const fromStart = (i: number): number =>
    start ? haversineMiles(start.lat, start.lng, stops[i].lat, stops[i].lng) : 0;

  // --- 1. greedy nearest-neighbour construction ---
  const visited = new Array<boolean>(n).fill(false);
  const tour: number[] = [];
  let current: number;
  if (start) {
    // begin at the stop nearest the driver
    current = 0;
    let best = fromStart(0);
    for (let i = 1; i < n; i++) {
      const m = fromStart(i);
      if (m < best) {
        best = m;
        current = i;
      }
    }
  } else {
    current = 0;
  }
  visited[current] = true;
  tour.push(current);
  while (tour.length < n) {
    let next = -1;
    let best = Infinity;
    for (let i = 0; i < n; i++) {
      if (!visited[i] && d[current][i] < best) {
        best = d[current][i];
        next = i;
      }
    }
    visited[next] = true;
    tour.push(next);
    current = next;
  }

  // --- 2. 2-opt refinement (uncross legs until no improvement) ---
  // Tour cost includes the fixed start leg (constant per first-stop choice)
  // and the return leg when roundTrip; 2-opt swaps interior segments.
  const legCost = (a: number, b: number): number => d[a][b];
  const tourCost = (t: number[]): number => {
    let total = start ? fromStart(t[0]) : 0;
    for (let i = 0; i < t.length - 1; i++) total += legCost(t[i], t[i + 1]);
    if (roundTrip && start) {
      total += fromStart(t[t.length - 1]);
    }
    return total;
  };

  let improved = true;
  let passes = 0;
  const MAX_PASSES = 50; // safety valve; small n converges in a handful
  while (improved && passes < MAX_PASSES) {
    improved = false;
    passes += 1;
    for (let i = 0; i < n - 1; i++) {
      for (let k = i + 1; k < n; k++) {
        // reverse tour[i..k]; accept if total drops
        const candidate = tour
          .slice(0, i)
          .concat(tour.slice(i, k + 1).reverse(), tour.slice(k + 1));
        if (tourCost(candidate) + 1e-9 < tourCost(tour)) {
          tour.splice(0, n, ...candidate);
          improved = true;
        }
      }
    }
  }

  // --- 3. assemble result ---
  const legs: OrderedRoute['legs'] = [];
  let totalMiles = 0;
  if (start) {
    const m = fromStart(tour[0]);
    legs.push({ fromId: null, toId: stops[tour[0]].id, miles: m });
    totalMiles += m;
  }
  for (let i = 0; i < n - 1; i++) {
    const m = d[tour[i]][tour[i + 1]];
    legs.push({
      fromId: stops[tour[i]].id,
      toId: stops[tour[i + 1]].id,
      miles: m,
    });
    totalMiles += m;
  }
  if (roundTrip && start) {
    const m = fromStart(tour[n - 1]);
    legs.push({ fromId: stops[tour[n - 1]].id, toId: 'start', miles: m });
    totalMiles += m;
  }

  return {
    order: tour.map((i) => stops[i].id),
    totalMiles,
    legs,
  };
}
